import { FormHelperText, TextField, InputAdornment, IconButton, Alert } from '@mui/material';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Box, Typography } from '@mui/material';
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ForgotPassword from './forgotPassword';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import { useFormik } from 'formik';
import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import { isServerUnavailableError } from '../services/appRuntime';
import { clearLoginFailures, noteLoginFailure, reportSessionEvent } from '../utils/sessionTrace';
import '../css/login.css';

export const Login = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [openForgotPass, setOpenForgotPass] = useState(false);
    const [msg, setMsg] = useState(null);
    const [sessionNotice, setSessionNotice] = useState(null);

    useEffect(() => {
        const sessionReason = searchParams.get('session');
        if (sessionReason === 'expired') {
            setSessionNotice('Votre session a expiré. Veuillez vous reconnecter.');
        } else if (sessionReason === 'invalid') {
            setSessionNotice('Votre session n\'est plus valide. Veuillez vous reconnecter.');
        }

        if (sessionReason) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('session');
            setSearchParams(nextParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const formik = useFormik({
        initialValues: {
            email: '',
            password_hash: '',
        },
        validationSchema: Yup.object().shape({
            email: Yup.string().email(t('emailFormatRequired')).required(t('emailRequied')),
            password_hash: Yup.string().required(t('passwordRequired')),
        }),
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: async (values) => {
            setLoading(true);
            const email = values.email?.toLowerCase().trim();
            void reportSessionEvent('ui.login.submit', { email, path: '/login' });
            try {
                const response = await axios.post("auth/signin", values, { withCredentials: true });
                if (response.status === 200 || response.status === 201) {
                    const { access_token, userData } = response.data;
                    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
                    localStorage.setItem("token", access_token);
                    window.dispatchEvent(new Event("storage"));
                    clearLoginFailures();
                    void reportSessionEvent('ui.login.success', { email, userId: userData, path: '/login' });
                    navigate("/dashboard/machines");
                }
            } catch (error) {
                const status = error?.response?.status;
                noteLoginFailure(email);
                void reportSessionEvent('ui.login.failure', {
                    level: 'warn',
                    email,
                    status,
                    message: error?.response?.data?.message || error.message,
                    path: '/login',
                });
                if (isServerUnavailableError(error)) {
                    setMsg('Le serveur est inaccessible pour le moment. Réessayez dans quelques instants.');
                } else {
                    setMsg(
                      typeof error?.response?.data === 'string'
                        ? error.response.data
                        : error?.response?.data?.message || error.message || 'Une erreur est survenue lors de la connexion.',
                    );
                }
                CustomErrorLogger(error, "Login Error");
                console.error("Login Error:", error);
            } finally {
                setLoading(false);
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors } = formik;

    return (
        <Page title="Login" className="loginMainBox" list={true}>
            <Box className="loginImageBox">
                <Typography className="loginImageFirstTypography">{t('compangnionsDuDevoir')}</Typography>
                <Typography className="loginImageSecondTypography">{t('optimiserLesJestions')}</Typography>
            </Box>
            <Box className="loginInputBox">
                <Box className="loginInputInner">
                    {sessionNotice && <Alert severity="info" className="loginNotice">{sessionNotice}</Alert>}
                    {msg && <Alert severity="error" className="loginNotice">{msg}</Alert>}
                    <Box className="loginInputTypographyBox">
                        <Typography className="loginInputFirstTypography">{t('Connexion')}</Typography>
                        <Typography className="loginInputSecondTypography">{t('saisissezVosInfo')}</Typography>
                    </Box>
                    <form onSubmit={handleSubmit} className="loginInputFormBox">
                        <Box>
                            <FormHelperText id="outlined-weight-helper-text" className="loginInputTitle">{t('adressEmail')}</FormHelperText>
                            <TextField
                                id="loginEmail"
                                name="email"
                                type="email"
                                fullWidth
                                required
                                className="customTextField"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Box>
                        <Box>
                            <FormHelperText id="outlined-weight-helper-text" className="loginInputTitle">{t('motDePass')}</FormHelperText>
                            <TextField
                                id="loginPassword"
                                name="password_hash"
                                type={showPassword ? "text" : "password"}
                                fullWidth
                                required
                                variant="outlined"
                                className="customTextField"
                                value={values.password_hash}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password_hash && Boolean(errors.password_hash)}
                                helperText={touched.password_hash && errors.password_hash}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword((show) => !show)} edge="end">
                                                <Iconify
                                                    icon={
                                                        showPassword
                                                            ? "eva:eye-fill"
                                                            : "eva:eye-off-fill"
                                                    }
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box className='loginActionsBox'>
                            <Typography className='loginForgotPassword' onClick={() => setOpenForgotPass(true)}>{t('forgotPassword')}</Typography>
                            <button type="submit" className="loginButtonConnection" disabled={loading}>
                                {loading ? `${t('Connexion')}…` : t('Connexion')}
                            </button>
                        </Box>
                    </form>
                </Box>
            </Box>

            {openForgotPass &&
                <ForgotPassword
                    openForgotPass={openForgotPass}
                    setOpenForgotPass={setOpenForgotPass}
                />
            }
        </Page>
    );
};